<script setup lang="ts">
import { money } from '@nuts/auto-delivery-sdk/dist/utils/format';
import { useScroll, whenever } from '@vueuse/core';
import { ref, useTemplateRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import { getPredictedShippingCutoff, ShippingCutoff } from '@/api/predictedShipping';
import Modal from '@/components/base/layout/Modal.vue';
import CloseModalButton from '@/components/base/layout/modal/CloseModalButton.vue';
import PredictedShippingCutoffText from '@/components/base/PredictedShippingCutoffText.vue';
import ActionBlock from '@/components/cart/added-modal/ActionBlock.vue';
import LineItem from '@/components/cart/added-modal/LineItem.vue';
import ShippingMessage from '@/components/cart/ShippingMessage.vue';
import ProductAddedBanner from '@/components/product-added/ProductAddedBanner.vue';
import ProductAddedContainer from '@/components/product-added/ProductAddedContainer.vue';
import ProductAddedHeader from '@/components/product-added/ProductAddedHeader.vue';
import ProductAddedRecommendations from '@/components/product-added/ProductAddedRecommendations.vue';
import { useCurrentRoute } from '@/composables/navigation/useCurrentRoute';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { useDelivery } from '@/composables/useDelivery';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { usePayment } from '@/composables/usePayment';
import { pluralizeBy } from '@/filters/pluralize';
import { useInitialRequest } from '@/stores/initialRequest';
import { formatPurchaseItem, gtag } from '@/utils/analytics';
import { toLegacySku } from '@/utils/product';

const props = defineProps<{ isOpen: boolean }>();

const emit = defineEmits(['handle-close']);

const route = useRoute();
const store = useStore();

const {
  addedAddOns: addOns,
  addedLineItem: lineItem,
  loadLineItemExpansions,
  lineItems,
} = useCart(store);
const { path } = useCurrentRoute(useInitialRequest().url, route);
const { freeShippingThreshold } = useDelivery(store);
const { estimatedAmountToCharge, estimatedTotal } = usePayment(store, ref(false));

const shippingCutoff = ref<ShippingCutoff>();
const scrollBarAtTop = ref(true);
const modalRef = useTemplateRef<HTMLElement>('modalRef');
const { y } = useScroll(modalRef);

const loadExpansions = useCallback(async () => {
  await loadLineItemExpansions();
});

const { flags } = useFeatureFlags();

const handleClose = () => {
  emit('handle-close');
};

whenever(
  () => lineItem.value?.id,
  async () => {
    if (props.isOpen) {
      await loadExpansions.execute();
      shippingCutoff.value = await getPredictedShippingCutoff();

      gtag('event', 'view_cart', {
        location: path.value,
        currency: 'USD',
        value: money(estimatedAmountToCharge?.value),
        items: lineItems.value.map(formatPurchaseItem),
      });
    }
  },
);

watch(y, () => {
  scrollBarAtTop.value = y.value === 0;
});
</script>

<template>
  <Modal
    anchor="right"
    aria-label="Added to cart"
    data-test="cart-added-modal"
    :hasControls="false"
    :hasDefaultPadding="false"
    :isOpen="isOpen && !!lineItem"
    width="md:max-w-xl"
    @handle-close="handleClose"
  >
    <template #close-button>
      <CloseModalButton
        v-if="scrollBarAtTop"
        class="absolute right-5"
        :class="shippingCutoff ? 'top-14' : 'top-5'"
        data-promo="1"
        data-promo-creative="Add to cart modal"
        data-promo-name="Closed by X"
        @click="handleClose"
      />
      <div v-else />
    </template>
    <template #body>
      <ProductAddedContainer v-if="lineItem" data-test="modal-content" ref="modalRef">
        <template #header>
          <ProductAddedBanner v-if="shippingCutoff" data-test="predicted-shipping-message">
            <PredictedShippingCutoffText :shippingCutoff />
          </ProductAddedBanner>
          <ProductAddedHeader theme="green">Added to Cart</ProductAddedHeader>
        </template>

        <template #body>
          <!-- Image, quantity and price -->
          <LineItem
            :lineItemWithQuantitySubset="lineItem"
            :loadExpansionsPending="loadExpansions.isPending"
            class="mt-4"
            @handle-close="handleClose"
          />

          <template v-if="addOns?.length">
            <LineItem
              v-for="addOn in addOns"
              :key="addOn.id"
              :lineItemWithQuantitySubset="addOn"
              :loadExpansionsPending="loadExpansions.isPending"
              class="mt-4"
              @handle-close="handleClose"
            />
          </template>

          <div
            :class="{
              'sticky bg-white z-20': !scrollBarAtTop,
            }"
          >
            <ShippingMessage
              :class="flags.cartLayoutUpdates ? 'mt-6 px-4' : 'mt-4'"
              :freeShippingThreshold
              stackVertically
              :totalPrice="estimatedTotal"
            />

            <div class="p-4 w-full bg-white">
              <ActionBlock @handle-close="handleClose">
                <template #proceed-message>
                  <span>Proceed to Checkout - </span>
                  <span> {{ lineItems.length }} {{ pluralizeBy('item', lineItems.length) }} </span>
                  <span> ({{ money(estimatedTotal) }})</span>
                </template>
              </ActionBlock>
            </div>
          </div>

          <ProductAddedRecommendations
            :dyPageContext="{
              type: 'CART',
              data: lineItems.map((l) => toLegacySku(l.variant.sku)),
            }"
            dySelector="[CONFIG] Cart Added - Rows"
            pageSection="Added to Cart Modal"
            @handle-close="handleClose"
          />
        </template>
      </ProductAddedContainer>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.right-anchor-height) {
  overflow: hidden;
}

.sticky {
  @include respond-min($screen-md-min) {
    top: 0px;
  }
}
</style>
